import { useEffect, useRef, useState } from "react";
import type { TrackerSnapshot } from "./api";

type TrackerMap = Record<string, TrackerSnapshot>;

interface UseTrackerResult {
  snapshots: TrackerMap;
  /** True once at least one batch has arrived since the last reset. */
  hasData: boolean;
  /** True while the EventSource is in the connecting state. */
  connecting: boolean;
}

/**
 * Subscribes to the `/api/track/stream` Server-Sent Events endpoint and
 * returns a map of the latest Snapshot keyed by ticker. Each SSE frame
 * is a JSON array of Snapshots; this hook merges them into the map so
 * the last-known value for each ticker is always available even if the
 * server sends a partial batch.
 *
 * The EventSource is created once on mount. `enabled=false` tears it
 * down — we don't want to open a stream until the user has run at
 * least one analyze, since the tracker has no baselines to emit
 * before then. The hook also closes the stream on unmount and on a
 * visibility change away from the tab (browsers cap the number of
 * concurrent EventSources per origin; leaving hidden tabs open drains
 * the pool).
 */
export function useTracker(enabled: boolean): UseTrackerResult {
  const [snapshots, setSnapshots] = useState<TrackerMap>({});
  const [connecting, setConnecting] = useState(false);
  const [hasData, setHasData] = useState(false);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!enabled) {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      return;
    }

    setConnecting(true);
    const es = new EventSource("/api/track/stream");
    esRef.current = es;

    es.onopen = () => setConnecting(false);
    es.onmessage = (ev) => {
      try {
        const batch: TrackerSnapshot[] = JSON.parse(ev.data);
        if (!Array.isArray(batch) || batch.length === 0) return;
        setSnapshots((prev) => {
          const next = { ...prev };
          for (const snap of batch) {
            next[snap.ticker] = snap;
          }
          return next;
        });
        setHasData(true);
      } catch (err) {
        // Malformed frame — log and keep the stream alive. A single
        // bad frame shouldn't kill the connection.
        console.warn("tracker: dropped bad frame", err);
      }
    };
    es.onerror = () => {
      // EventSource auto-reconnects on its own (respects the server's
      // `retry:` hint). We just reflect the transient disconnect
      // in the UI state; the browser handles the backoff.
      setConnecting(true);
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [enabled]);

  return { snapshots, hasData, connecting };
}
